import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { partnerApiService } from '../services/api';
import { DashboardReview, DashboardStats } from '../types/api';
import { useAuth } from './AuthContext';

/**
 * Общее состояние кабинета, которое живёт над экранами.
 *
 * Появился вместе с дизайном 2.0: в новой шапке есть поиск и счётчики на
 * вкладках, а значит и строка запроса, и сводка дашборда нужны сразу
 * нескольким экранам. Раньше дашборд грузила только «Главная» — теперь он
 * загружается один раз здесь, и «Главная» берёт готовое.
 */
interface CabinetContextValue {
  /** Строка из поиска в шапке. Каждый экран фильтрует ею своё содержимое. */
  query: string;
  setQuery: (next: string) => void;
  stats: DashboardStats | null;
  reviews: DashboardReview[];
  setReviews: React.Dispatch<React.SetStateAction<DashboardReview[]>>;
  isDashboardLoading: boolean;
  refreshDashboard: () => Promise<void>;
}

const CabinetContext = createContext<CabinetContextValue | null>(null);

export const CabinetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [query, setQuery] = useState('');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [reviews, setReviews] = useState<DashboardReview[]>([]);
  const [isDashboardLoading, setIsDashboardLoading] = useState(true);

  const refreshDashboard = useCallback(async () => {
    if (!isAuthenticated) {
      setIsDashboardLoading(false);
      return;
    }
    try {
      setIsDashboardLoading(true);
      const data = await partnerApiService.getDashboard();
      setStats(data.stats ?? null);
      // Дашборд может не вернуть отзывы — тогда их догрузит «Главная»
      // из публичного эндпоинта. Пустым массивом уже загруженное не затираем.
      if (data.recent_reviews?.length) setReviews(data.recent_reviews);
    } catch (error) {
      console.error('Ошибка загрузки дашборда:', error);
    } finally {
      setIsDashboardLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refreshDashboard();
  }, [refreshDashboard]);

  const value = useMemo<CabinetContextValue>(
    () => ({ query, setQuery, stats, reviews, setReviews, isDashboardLoading, refreshDashboard }),
    [query, stats, reviews, isDashboardLoading, refreshDashboard],
  );

  return <CabinetContext.Provider value={value}>{children}</CabinetContext.Provider>;
};

export function useCabinet(): CabinetContextValue {
  const ctx = useContext(CabinetContext);
  if (!ctx) throw new Error('useCabinet должен вызываться внутри <CabinetProvider>');
  return ctx;
}
